/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type { EntityIdKind } from '@sportninja/common/features/competition/operations/utils/entityIdLabel'
import { entityIdLabel } from '@sportninja/common/features/competition/operations/utils/entityIdLabel'
import {
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'
import Icon from 'src/components/Icon'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'

interface EntityIdChipProps {
  kind: EntityIdKind
  id: string
}

/**
 * One "SN Team ID: <uid> [copy]" row. Carried over from the legacy Setup tab's
 * `EditEntitySubTitle`, including its tenant-aware label and the 2s "Copied"
 * confirmation, at the smaller scale this header needs.
 */
const EntityIdChip = ({ kind, id }: EntityIdChipProps) => {
  'use memo'

  const { hasCopied, copy } = useCopyToClipboard()
  const label = entityIdLabel(kind)

  return (
    <div css={rowStyle}>
      <span css={labelStyle}>{label}:</span>
      <div css={valueBoxStyle}>
        <span css={valueStyle}>{hasCopied ? 'Copied' : id}</span>
        <button
          type='button'
          title={`Copy ${label} to clipboard`}
          aria-label={`Copy ${label} to clipboard`}
          css={copyButtonStyle}
          onClick={() => copy(id)}
        >
          <Icon
            faType='far'
            name={hasCopied ? 'clipboard-check' : 'clipboard'}
            color={colors.PRIMARY}
            fontSize={13}
          />
        </button>
      </div>
    </div>
  )
}

const rowStyle = css`
  ${paragraphXSmall}
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors.NEUTRAL_100};
`

const labelStyle = css`
  ${weightSemiBold}
  white-space: nowrap;
`

const valueBoxStyle = css`
  display: flex;
  align-items: center;
  border-radius: 4px;
  background: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_200};
  overflow: hidden;
`

const valueStyle = css`
  ${paragraphSmall}
  padding: 3px 8px;
  /* After the preset: it sets the tenant body font, and uids read as code. */
  font-family: 'Courier New', monospace;
  color: ${colors.NEUTRAL_0};
  /* Holds width so swapping the uid for "Copied" does not shift the layout. */
  min-width: 150px;
`

const copyButtonStyle = css`
  border-left: 1px solid ${colors.SECONDARY_200};
  align-self: stretch;
  padding: 3px 7px;
  display: flex;
  align-items: center;
  cursor: pointer;
`

export default EntityIdChip
